/**
 * Cliente HTTP del panel: guarda los tokens, renueva el de acceso cuando
 * expira y traduce los errores de la API a algo que se pueda mostrar.
 */

const CLAVE_ACCESS = 'gestor-doc-access';
const CLAVE_REFRESH = 'gestor-doc-refresh';

export const tokens = {
  access: () => localStorage.getItem(CLAVE_ACCESS),
  refresh: () => localStorage.getItem(CLAVE_REFRESH),
  guardar(access: string, refresh: string) {
    localStorage.setItem(CLAVE_ACCESS, access);
    localStorage.setItem(CLAVE_REFRESH, refresh);
  },
  limpiar() {
    localStorage.removeItem(CLAVE_ACCESS);
    localStorage.removeItem(CLAVE_REFRESH);
  },
};

export class ErrorApi extends Error {
  constructor(
    public readonly estado: number,
    public readonly codigo: string,
    mensaje: string,
    public readonly detalles?: unknown,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }

  /** Errores de validación campo por campo, para pintarlos en el formulario. */
  get porCampo(): Record<string, string> {
    if (!Array.isArray(this.detalles)) return {};
    const salida: Record<string, string> = {};
    for (const d of this.detalles as Array<{ campo?: string; mensaje?: string }>) {
      if (d.campo && d.mensaje) salida[d.campo] = d.mensaje;
    }
    return salida;
  }
}

let renovacionEnCurso: Promise<boolean> | null = null;

async function renovar(): Promise<boolean> {
  const refreshToken = tokens.refresh();
  if (!refreshToken) return false;

  // Varias consultas pueden recibir 401 a la vez; se renueva una sola vez.
  renovacionEnCurso ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const { data } = await res.json();
      tokens.guardar(data.access, data.refresh);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (renovacionEnCurso = null), 0);
    }
  })();

  return renovacionEnCurso;
}

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  cuerpo?: unknown;
  reintentar?: boolean;
}

export async function api<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const { metodo = 'GET', cuerpo, reintentar = true } = opciones;
  const access = tokens.access();

  const res = await fetch(`/api${ruta}`, {
    method: metodo,
    headers: {
      ...(cuerpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
    },
    ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
  });

  if (res.status === 401 && reintentar && (await renovar())) {
    return api<T>(ruta, { ...opciones, reintentar: false });
  }

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    const error = payload?.error;
    if (res.status === 401) {
      tokens.limpiar();
      window.dispatchEvent(new CustomEvent('sesion-caida'));
    }
    throw new ErrorApi(
      res.status,
      error?.codigo ?? 'ERROR',
      error?.mensaje ?? 'No se pudo completar la operación',
      error?.detalles,
    );
  }

  if (res.status === 204) return undefined as T;
  const payload = await res.json();
  return payload.data as T;
}

/**
 * Subida con progreso.
 *
 * `fetch` no informa el avance del envío, y sin barra de progreso una subida
 * de 200 MB es indistinguible de una aplicación colgada. Por eso, y solo por
 * eso, acá se usa XMLHttpRequest.
 */
export function subir<T>(
  ruta: string,
  formulario: FormData,
  alAvanzar?: (porcentaje: number) => void,
): { promesa: Promise<T>; cancelar: () => void } {
  const xhr = new XMLHttpRequest();
  const promesa = new Promise<T>((cumplir, fallar) => {
    xhr.open('POST', `/api${ruta}`);
    const access = tokens.access();
    if (access) xhr.setRequestHeader('Authorization', `Bearer ${access}`);

    xhr.upload.onprogress = (evento) => {
      if (evento.lengthComputable && alAvanzar) alAvanzar(Math.round((evento.loaded / evento.total) * 100));
    };
    xhr.onload = () => {
      let payload: { data?: T; error?: { codigo: string; mensaje: string; detalles?: unknown } } | null = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        cumplir(payload?.data as T);
      } else {
        fallar(
          new ErrorApi(
            xhr.status,
            payload?.error?.codigo ?? 'ERROR',
            payload?.error?.mensaje ?? 'No se pudo subir el archivo',
            payload?.error?.detalles,
          ),
        );
      }
    };
    xhr.onerror = () => fallar(new ErrorApi(0, 'RED', 'Se cortó la conexión durante la subida'));
    xhr.onabort = () => fallar(new ErrorApi(0, 'CANCELADO', 'Subida cancelada'));
    xhr.send(formulario);
  });

  return { promesa, cancelar: () => xhr.abort() };
}

/**
 * URL de descarga o vista previa. Lleva el token en la query porque una
 * pestaña nueva o una etiqueta `<img>` no mandan cabecera Authorization; la
 * API solo lo acepta en estas dos rutas de lectura.
 */
export function urlArchivo(nodoId: string, modo: 'descargar' | 'vista', version?: number): string {
  const parametros = new URLSearchParams({ token: tokens.access() ?? '' });
  if (version) parametros.set('version', String(version));
  return `/api/nodos/${nodoId}/${modo}?${parametros}`;
}
